// Shared helpers for the agent "What's New" intro banner.
//
// A single source of truth for how the dated update notice is rendered into the
// agent's welcome/intro message. Used by App.tsx (initial welcome build) and by
// ChatInterface.tsx (inline admin edit → instant banner refresh) so the two
// never drift.

export interface WhatsNewNotice {
  enabled?: boolean;
  date?: string;
  summary?: string;
}

// Leading marker + trailing separator that delimit the banner inside the
// welcome message content. Kept in sync with formatWhatsNewBlock below.
export const WHATS_NEW_PREFIX = "## What's New";
const WHATS_NEW_SEPARATOR = '\n\n---\n\n';

/**
 * Build the Markdown banner for an update notice, or '' when there's no summary.
 * Renders a large heading followed by the summary. Note: this is intentionally
 * driven by the summary text, NOT the `enabled` flag — `enabled` only controls
 * the home-screen UPDATED badge / float, while any filled-in summary shows in
 * the intro banner (matching the editor's "shown in the intro message" label).
 */
export function formatWhatsNewBlock(notice?: WhatsNewNotice | null): string {
  if (!notice) return '';
  const summary = (notice.summary || '').trim();
  if (!summary) return '';
  const dateSuffix = notice.date ? ` — ${notice.date}` : '';
  return `${WHATS_NEW_PREFIX}${dateSuffix}\n\n${summary}${WHATS_NEW_SEPARATOR}`;
}

/**
 * Remove a leading What's New banner from welcome content (if present), leaving
 * the underlying welcome text untouched.
 */
export function stripLeadingWhatsNewBlock(content: string): string {
  if (!content || !content.startsWith(WHATS_NEW_PREFIX)) return content;
  const idx = content.indexOf(WHATS_NEW_SEPARATOR);
  if (idx === -1) return content;
  return content.slice(idx + WHATS_NEW_SEPARATOR.length);
}

/**
 * Replace (or remove) the What's New banner at the top of a welcome message so
 * it reflects the given notice. Idempotent: strips any existing banner first.
 */
export function applyWhatsNew(content: string, notice?: WhatsNewNotice | null): string {
  return formatWhatsNewBlock(notice) + stripLeadingWhatsNewBlock(content);
}
